/**
 * List Campaigns hook.
 *
 * `useFetchCampaigns` — in-component hook that fetches campaigns with loading state.
 * Renders the page immediately with a spinner while data loads.
 *
 * The hook accepts a URL slug (e.g. 'cashback', 'no-cost-emi') and translates it
 * to the corresponding CampaignType enum value via SLUG_TO_CAMPAIGN_TYPE before
 * making the API call. This keeps the URL structure decoupled from the API contract.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { CampaignStatus, CampaignType } from 'src/@types/valueMatching/enums';
import { CampaignTableRow } from 'src/@types/valueMatching/campaign';
import { fetchListCampaigns } from 'src/accessors/valueMatchingAccessor';
import { toCampaignTableRows } from 'src/utils/valueMatching/list/campaignListUtils';
import { SLUG_TO_CAMPAIGN_TYPE, CAMPAIGN_CONFIG } from 'src/routes/valueMatching/routes';
import { logError } from 'src/utils/monitoring';

export { isValidCampaignType } from 'src/utils/valueMatching/commonUtils';

/** Generic fallback message shown to users when an API error occurs */
const GENERIC_ERROR_MESSAGE = 'Failed to load campaigns. Please try again later.';

/**
 * Extracts a user-safe error message from an unknown error value.
 * Avoids exposing raw API error details to the UI.
 *
 * @param err - The caught error value
 * @returns A sanitized error message string
 */
function getSafeErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    logError(err.message, { methodName: 'useFetchCampaigns' });
    return GENERIC_ERROR_MESSAGE;
  }
  if (typeof err === 'string') {
    logError(err, { methodName: 'useFetchCampaigns' });
    return GENERIC_ERROR_MESSAGE;
  }
  logError(String(err), { methodName: 'useFetchCampaigns' });
  return GENERIC_ERROR_MESSAGE;
}

// ============================================================================
// In-component fetch hook (non-blocking — shows spinner)
// ============================================================================

/** Shape of the data returned by useFetchCampaigns */
export interface UseFetchCampaignsResult {
  campaigns: CampaignTableRow[];
  campaignType: CampaignType | undefined;
  campaignTypeName: string;
  routeSegment: string;
  loading: boolean;
  error: string | null;
  /** Triggers a re-fetch of the campaigns list. Stable across renders. */
  refetch: () => void;
  /**
   * Optimistically patches the status of a single row in the local cache.
   * Used after irreversible mutations (e.g. cancel) where the BFF list query
   * may be eventually consistent and a fresh refetch can still return the old
   * status for a few seconds. The next successful refetch will override.
   */
  updateCampaignStatus: (campaignId: string, version: number, status: CampaignStatus) => void;
}

/**
 * Hook that fetches campaigns based on a URL slug.
 * Renders the page immediately and shows a loading spinner in the table.
 *
 * The slug is the URL path segment (e.g. 'cashback', 'no-cost-emi').
 * It is translated to a CampaignType enum value via SLUG_TO_CAMPAIGN_TYPE,
 * which is then sent to the API.
 *
 * @param slug - URL slug from route params (e.g. 'cashback', 'no-cost-emi').
 *               Pass `undefined` or an empty string if the param is not yet available.
 * @returns Campaigns, loading state, campaign type info, and error state
 *
 * @example
 * ```tsx
 * const { slug } = useParams();
 * const { campaigns, loading, campaignTypeName, error } = useFetchCampaigns(slug ?? '');
 * ```
 */
export function useFetchCampaigns(slug: string | undefined): UseFetchCampaignsResult {
  const safeSlug = slug ?? '';
  // Translate URL slug → CampaignType enum using the centralized config
  const campaignType = SLUG_TO_CAMPAIGN_TYPE[safeSlug];
  const campaignTypeName = campaignType
    ? CAMPAIGN_CONFIG[campaignType].displayName
    : ''; // invalid slug → no meaningful name; component should show the error state instead

  const [serverCampaigns, setServerCampaigns] = useState<CampaignTableRow[]>([]);
  // Only show loading spinner when we have a valid campaign type to fetch.
  // Initialising to false for invalid/unknown slugs avoids a spurious spinner flash.
  const [loading, setLoading] = useState(!!campaignType);
  const [error, setError] = useState<string | null>(null);
  // Incrementing this counter re-triggers the fetch effect below, giving
  // consumers a manual refetch capability (e.g. after a successful cancel).
  const [refreshCounter, setRefreshCounter] = useState(0);

  // Optimistic status overrides keyed by `${id}::${version}`. Kept SEPARATE
  // from `serverCampaigns` so a concurrent in-flight fetch resolving after
  // the override is applied can't blow it away. Merged during render below.
  // Cleared when the slug changes (different campaign type → fresh page).
  const [statusOverrides, setStatusOverrides] = useState<Record<string, CampaignStatus>>({});

  // Use ref to track the AbortController for proper request cancellation on cleanup
  const abortControllerRef = useRef<AbortController | null>(null);

  const refetch = useCallback(() => {
    setRefreshCounter((c) => c + 1);
  }, []);

  // Apply a local-only status patch for the matching (campaignId, version) row. Survives across refetches until the BFF returns a row whose own status
  // already matches the override (in which case we drop it on next render),
  // OR until the user navigates to a different campaign type (slug change).
  const updateCampaignStatus = useCallback(
    (campaignId: string, version: number, status: CampaignStatus) => {
      setStatusOverrides((prev) => ({ ...prev, [`${campaignId}::${version}`]: status }));
    },
    [],
  );

  // Drop overrides when the user switches campaign types — they only apply to the slug that was active when the mutation happened.
  useEffect(() => {
    setStatusOverrides({});
  }, [safeSlug]);

  useEffect(() => {
    if (!campaignType) {
      setError(`Unknown campaign type: ${slug}`);
      setLoading(false);
      setServerCampaigns([]);
      return;
    }

    // Cancel any in-flight request from a previous render
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    let cancelled = false;

    const loadCampaigns = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchListCampaigns({ campaignType, signal: abortController.signal });
        if (!cancelled) {
          setServerCampaigns(toCampaignTableRows(response.campaigns ?? []));
        }
      } catch (err) {
        if (!cancelled) {
          setServerCampaigns([]);
          setError(getSafeErrorMessage(err));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadCampaigns();

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [campaignType, safeSlug, refreshCounter]);

  // Merge layer: apply optimistic overrides on top of server data.
  // Overrides whose status already matches the server-authoritative row are
  // collected as `staleKeys` and dropped in a follow-up effect so the override
  // map doesn't grow across the session. Doing the cleanup in a separate
  // effect (rather than inside the memo) avoids setState during render.
  const { campaigns, staleOverrideKeys } = useMemo(() => {
    if (Object.keys(statusOverrides).length === 0) {
      return { campaigns: serverCampaigns, staleOverrideKeys: [] as string[] };
    }
    const stale: string[] = [];
    const merged = serverCampaigns.map((row) => {
      const key = `${row.id}::${row.version}`;
      const override = statusOverrides[key];
      if (!override) return row;
      if (override === row.status) {
        // Server has caught up — mark this override for cleanup.
        stale.push(key);
        return row;
      }
      return { ...row, status: override };
    });
    return { campaigns: merged, staleOverrideKeys: stale };
  }, [serverCampaigns, statusOverrides]);

  // Drop stale overrides identified by the merge above. Depend on the joined
  // key list (stable string) so the effect only runs when the set of stale
  // keys actually changes — that breaks the loop with the merge memo, which
  // would otherwise re-run whenever `statusOverrides` shrinks.
  const staleKeysJoined = staleOverrideKeys.join('|');
  const staleKeysRef = useRef(staleOverrideKeys);
  staleKeysRef.current = staleOverrideKeys;
  useEffect(() => {
    if (staleKeysRef.current.length === 0) return;
    const keys = staleKeysRef.current;
    setStatusOverrides((prev) => {
      const next = { ...prev };
      keys.forEach((key) => delete next[key]);
      return next;
    });
  }, [staleKeysJoined]);

  return {
    campaigns,
    campaignType,
    campaignTypeName,
    routeSegment: safeSlug,
    loading,
    error,
    refetch,
    updateCampaignStatus,
  };
}
